import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "loading-state"
};
const _hoisted_2 = {
    key: 0,
    class: "walkthrough-section"
};
const _hoisted_3 = {
    key: 1,
    class: "walkthrough-section"
};
const _hoisted_4 = { class: "folder-toolbar" };
const _hoisted_5 = {
    key: 0,
    class: "folder-tile"
};
const _hoisted_6 = {
    class: "folder-drag-handle",
    "aria-label": "Drag to reorder"
};
const _hoisted_7 = { class: "group-label" };
const _hoisted_8 = ["onClick"];
const _hoisted_9 = { class: "doc-info" };
const _hoisted_10 = { class: "doc-company" };
const _hoisted_11 = { class: "doc-filename" };
const _hoisted_12 = { class: "doc-added" };
const _hoisted_13 = {
    key: 0,
    class: "doc-label"
};
const _hoisted_14 = ["src"];
import { ref, computed, watch, onMounted, nextTick, getCurrentInstance, inject } from 'vue';
import { GridLayout, GridItem } from 'grid-layout-plus';
import { useAppState } from '@/composables/useAppState';
import { useCustomer } from '@/composables/useCustomer';
import { useDocuments } from '@/composables/useDocuments';
import { useContent } from '@/composables/useContent';
import { useWhatsApp } from '@/composables/useWhatsApp';
import SettingsOverlay from '../components/SettingsOverlay.vue';
import DocumentDialog from '../components/DocumentDialog.vue';
import AccountTypeFilter from '@/components/elements/AccountTypeFilter.vue';
import DocCategoryCard from '@/components/elements/DocCategoryCard.vue';
import AiChatBanner from '@/components/elements/AiChatBanner.vue';
import FaqsSection from '@/components/FaqsSection.vue';
import WhatsAppEnableDrawer from '@/components/elements/WhatsAppEnableDrawer.vue';
import MGWalkthrough from '@/components/elements/MGWalkthrough.vue';
import DocumentTypeGrid from '@/components/elements/DocumentTypeGrid.vue';
const FOLDER_LAYOUT_KEY = 'docs_folder_layout_v1';
const FOLDER_COMPACT_KEY = 'docs_folder_compact';
// Compact mode shrinks each tile (4 per row, shorter) vs comfortable (3 per row).
export default /*@__PURE__*/ _defineComponent({
    __name: 'DocumentsPage',
    emits: ["colour-mode-changed"],
    setup(__props, { emit: __emit }) {
        const state = useAppState();
        const { getCustomer, savePreferences } = useCustomer();
        const { getDocuments, getDocumentTypes } = useDocuments();
        const { getWalkthrough, getFaqs: getContentFaqs } = useContent();
        const instance = getCurrentInstance();
        const cookies = instance?.appContext.config.globalProperties.$cookies;
        const whatsAppNumber = instance?.appContext.config.globalProperties.$whatsAppNumber || '';
        const registerSidebar = inject('registerSidebar', null);
        const emit = __emit;
        // ---------- Desktop chrome ----------
        const colourMode = ref('light');
        const showSettings = ref(false);
        const customer = computed(() => state.customer);
        const safeTop = computed(() => parseInt(cookies?.get('st') ?? 0));
        function updatePref(key, value) {
            if (state.customer?.preferences) {
                state.customer.preferences[key] = value;
                savePreferences();
            }
        }
        function selectColourMode(mode) {
            colourMode.value = mode;
            localStorage.setItem('mg_colour_mode', mode);
            emit('colour-mode-changed', mode);
        }
        // ---------- WhatsApp ----------
        const whatsAppPromoCard = ref(null);
        const whatsAppEnableDrawer = ref(null);
        const { handleWhatsAppAction, handleEnableWhatsApp, handleWhatsAppEnableDone, handleWhatsAppDrawerClosed } = useWhatsApp({
            whatsAppPromoCard,
            whatsAppEnableDrawer,
            whatsAppNumber,
        });
        // ---------- Documents data ----------
        const activeTab = ref('all');
        const documentTypes = ref(null);
        const faqs = ref([]);
        const heroWalkthroughData = ref(null);
        const docDialog = ref(null);
        const selectedDocId = ref('');
        const documentCategoryImageMap = {
            'Home': require('@/assets/doc-categories/home.png'),
            'Family': require('@/assets/doc-categories/family.png'),
            'Banking': require('@/assets/doc-categories/banking.png'),
            'Investments': require('@/assets/doc-categories/investments.png'),
            'Vehicles': require('@/assets/doc-categories/vehicle.png'),
            'Travel': require('@/assets/doc-categories/travel.png'),
            'Work': require('@/assets/doc-categories/work.png'),
            'Tax': require('@/assets/doc-categories/tax.png'),
            'Health': require('@/assets/doc-categories/health.png'),
            'Subscriptions': require('@/assets/doc-categories/subscriptions.png'),
            'Personal': require('@/assets/doc-categories/personal.png'),
            'Pets': require('@/assets/doc-categories/pets.png'),
            'Important': require('@/assets/doc-categories/important.png'),
        };
        const defaultCategoryImage = require('@/assets/doc-categories/other.png');
        function getDocumentCategoryImage(category) {
            return documentCategoryImageMap[category] || defaultCategoryImage;
        }
        const hasDocuments = computed(() => state.documents && state.documents.length > 0);
        const filterTabs = computed(() => {
            const tabs = [{ value: 'all', label: 'All' }];
            if (documentTypes.value) {
                const seen = new Set();
                const categories = [];
                documentTypes.value.forEach((t) => {
                    const category = t.path?.split(' > ')[1];
                    if (category && !seen.has(category)) {
                        seen.add(category);
                        categories.push(category);
                    }
                });
                const counts = categoryCounts.value;
                categories.sort((a, b) => (counts[b] || 0) - (counts[a] || 0) || a.localeCompare(b));
                categories.forEach(c => tabs.push({ value: c, label: c }));
            }
            return tabs;
        });
        const categoryCounts = computed(() => {
            if (!state.documents)
                return {};
            const counts = {};
            state.documents.forEach((doc) => {
                const category = getDocCategory(doc);
                counts[category] = (counts[category] || 0) + 1;
            });
            return counts;
        });
        const categoryCards = computed(() => {
            const counts = categoryCounts.value;
            const categories = filterTabs.value.filter(t => t.value !== 'all').map(t => t.value);
            const withDocs = categories.filter(name => counts[name]).sort((a, b) => counts[b] - counts[a] || a.localeCompare(b));
            const withoutDocs = categories.filter(name => !counts[name]);
            return [...withDocs, ...withoutDocs].map(name => ({
                label: name, tabValue: name, illustration: getDocumentCategoryImage(name)
            }));
        });
        const cardsById = computed(() => Object.fromEntries(categoryCards.value.map(c => [c.tabValue, c])));
        // ---------- Folder reorder (grid-layout-plus: draggable, NOT resizable) ----------
        const compact = ref(localStorage.getItem(FOLDER_COMPACT_KEY) === 'true');
        const folderW = computed(() => compact.value ? 3 : 4); // 12-col grid → 4 / 3 per row
        const folderH = computed(() => compact.value ? 9 : 12); // tile height in row units
        const folderPositions = ref(loadFolderPositions());
        const folderLayout = ref([]);
        function loadFolderPositions() {
            try {
                const saved = JSON.parse(localStorage.getItem(FOLDER_LAYOUT_KEY) || 'null');
                if (saved && typeof saved === 'object')
                    return saved;
            }
            catch { /* ignore malformed */ }
            return {};
        }
        function defaultFolderPos(index) {
            const w = folderW.value;
            const perRow = Math.floor(12 / w);
            return { x: (index % perRow) * w, y: Math.floor(index / perRow) * folderH.value, w, h: folderH.value };
        }
        // Build the visible tile list. Comfortable mode honours the user's saved order;
        // compact mode ignores it and flows cleanly at 4 per row (the saved positions are
        // laid out for 3-per-row columns, which would leave the 4th column empty).
        function rebuildFolderLayout() {
            const w = folderW.value;
            const h = folderH.value;
            folderLayout.value = categoryCards.value.map((card, idx) => {
                const base = defaultFolderPos(idx);
                const saved = compact.value ? null : folderPositions.value[card.tabValue];
                const x = Math.min(saved ? saved.x : base.x, 12 - w);
                const y = saved ? saved.y : base.y;
                return { i: card.tabValue, x, y, w, h };
            });
        }
        // Persist + re-flow when compact mode changes.
        watch(compact, () => {
            localStorage.setItem(FOLDER_COMPACT_KEY, String(compact.value));
            rebuildFolderLayout();
        });
        function onFolderLayoutUpdated(updated) {
            // Compact mode is a transient flowed view — don't let its layout overwrite the
            // saved comfortable order.
            if (compact.value)
                return;
            for (const it of updated)
                folderPositions.value[it.i] = { x: it.x, y: it.y, w: it.w, h: it.h };
            localStorage.setItem(FOLDER_LAYOUT_KEY, JSON.stringify(folderPositions.value));
        }
        function resetFolderOrder() {
            folderPositions.value = {};
            localStorage.removeItem(FOLDER_LAYOUT_KEY);
            rebuildFolderLayout();
        }
        watch(() => categoryCards.value.map(c => c.tabValue).join(), rebuildFolderLayout, { immediate: true });
        const groupedDocuments = computed(() => {
            if (!state.documents)
                return [];
            const filtered = state.documents.filter((doc) => getDocCategory(doc) === activeTab.value);
            const sorted = filtered.sort((a, b) => new Date(b.createDateTime).getTime() - new Date(a.createDateTime).getTime());
            const groups = [];
            const map = {};
            sorted.forEach((doc) => {
                const label = getDocGroupLabel(doc);
                if (!map[label]) {
                    map[label] = { label, docs: [] };
                    groups.push(map[label]);
                }
                map[label].docs.push(doc);
            });
            return groups;
        });
        function getDocPathParts(doc) {
            const path = doc.sysoDocumentType?.path;
            if (!path)
                return null;
            return path.split(' > ');
        }
        function getDocCategory(doc) {
            const parts = getDocPathParts(doc);
            if (!parts || parts.length < 2)
                return 'Other';
            return parts[1];
        }
        function getDocGroupLabel(doc) {
            const parts = getDocPathParts(doc);
            return parts && parts.length >= 3 ? parts[2] : doc.sysoDocumentType?.name || doc.documentType || 'Other';
        }
        function properCase(str) {
            if (!str)
                return '';
            return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
        }
        function openDoc(id) {
            selectedDocId.value = id;
            registerDocsSidebar();
            // Wait for the documentId prop to propagate before the dialog reads it.
            nextTick(() => docDialog.value?.openDialog());
        }
        function onDocChanged() {
            getDocuments(() => registerDocsSidebar(), /* refreshCache */ true);
        }
        // Most-recent documents, shaped as sidebar page items (same pattern as the
        // recurring-payments / recent-chats sidebar). Clicking one opens the dialog.
        function registerDocsSidebar() {
            if (!registerSidebar)
                return;
            const items = [...(state.documents || [])]
                .sort((a, b) => new Date(b.createDateTime).getTime() - new Date(a.createDateTime).getTime())
                .slice(0, 8)
                .map((doc) => ({
                id: doc.id,
                title: properCase(doc.company || doc.documentSubject) || doc.documentName,
                subtitle: doc.documentName,
                meta: doc.createDateTimeString ? 'Added ' + doc.createDateTimeString : null,
            }));
            registerSidebar({
                config: {
                    actionButton: { icon: 'mdi-whatsapp', label: 'Send a document' },
                    pageItemsTitle: items.length ? 'Recent documents' : null,
                    pageItems: items,
                    currentPageItemId: selectedDocId.value || null,
                },
                data: {},
                handlers: {
                    onActionClick: () => { handleWhatsAppAction(); },
                    onPageItemClick: (id) => { openDoc(id); },
                    onPageItemDelete: () => { },
                    onPageItemRename: () => { },
                    onOpenSettings: () => { showSettings.value = true; }
                }
            });
        }
        function onLoad() {
            getCustomer(() => {
                getDocuments(() => registerDocsSidebar());
            });
            getDocumentTypes((types) => {
                documentTypes.value = types;
            });
            getContentFaqs('document-faqs', (result) => {
                faqs.value = result?.faqItems || [];
            });
            getWalkthrough('document-hero', (result) => {
                heroWalkthroughData.value = result;
            });
        }
        onMounted(() => {
            colourMode.value = localStorage.getItem('mg_colour_mode') || 'light';
            onLoad();
            registerDocsSidebar();
        });
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            const _component_v_icon = _resolveComponent("v-icon");
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["desktop-documents-page", colourMode.value])
            }, [
                (showSettings.value)
                    ? (_openBlock(), _createBlock(SettingsOverlay, {
                        key: 0,
                        customer: customer.value,
                        colourMode: colourMode.value,
                        safeTop: safeTop.value,
                        onClose: _cache[0] || (_cache[0] = ($event) => (showSettings.value = false)),
                        onUpdatePref: updatePref,
                        onSelectColourMode: selectColourMode
                    }, null, 8, ["customer", "colourMode", "safeTop"]))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        (!(_unref(state).customer && _unref(state).documents))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                                _createVNode(_component_v_skeleton_loader, {
                                    type: "card",
                                    height: "120px",
                                    class: "mb-4"
                                }),
                                _createVNode(_component_v_skeleton_loader, {
                                    type: "card",
                                    height: "300px",
                                    class: "mb-4"
                                })
                            ]))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createVNode(AiChatBanner),
                                (!hasDocuments.value)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        (!heroWalkthroughData.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                                                _createVNode(_component_v_skeleton_loader, {
                                                    type: "card",
                                                    height: "240px"
                                                })
                                            ]))
                                            : _createCommentVNode("", true),
                                        (heroWalkthroughData.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                                _createVNode(MGWalkthrough, {
                                                    walkthroughData: heroWalkthroughData.value,
                                                    variant: "sheet",
                                                    compact: true,
                                                    dotsAbove: false,
                                                    imageHeight: '220',
                                                    height: "240px",
                                                    onActionClicked: _unref(handleWhatsAppAction)
                                                }, null, 8, ["walkthroughData", "onActionClicked"])
                                            ]))
                                            : _createCommentVNode("", true),
                                        _cache[4] || (_cache[4] = _createElementVNode("p", { class: "what-can-add-heading" }, "What can I add?", -1)),
                                        _cache[5] || (_cache[5] = _createElementVNode("p", { class: "what-can-add-subtext" }, "Send documents via WhatsApp and we'll organise them for you", -1)),
                                        _createVNode(DocumentTypeGrid, { class: "document-type-grid-section" }),
                                        (faqs.value.length)
                                            ? (_openBlock(), _createBlock(FaqsSection, {
                                                key: 2,
                                                class: "faq-section",
                                                faqs: faqs.value,
                                                limit: 5,
                                                faqName: "document-faqs"
                                            }, null, 8, ["faqs"]))
                                            : _createCommentVNode("", true)
                                    ], 64))
                                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                        _createVNode(AccountTypeFilter, {
                                            tabs: filterTabs.value,
                                            modelValue: activeTab.value,
                                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((activeTab).value = $event))
                                        }, null, 8, ["tabs", "modelValue"]),
                                        (activeTab.value === 'all')
                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                _createElementVNode("div", _hoisted_4, [
                                                    _createElementVNode("button", {
                                                        class: "reset-order-btn",
                                                        onClick: _cache[2] || (_cache[2] = ($event) => (compact.value = !compact.value))
                                                    }, [
                                                        _createVNode(_component_v_icon, { size: "16" }, {
                                                            default: _withCtx(() => [
                                                                _createTextVNode(_toDisplayString(compact.value ? 'mdi-view-grid-outline' : 'mdi-view-comfy-outline'), 1)
                                                            ]),
                                                            _: 1
                                                        }),
                                                        _createElementVNode("span", null, _toDisplayString(compact.value ? 'Comfortable' : 'Compact'), 1)
                                                    ]),
                                                    _createElementVNode("button", {
                                                        class: "reset-order-btn",
                                                        onClick: resetFolderOrder
                                                    }, [
                                                        _createVNode(_component_v_icon, { size: "16" }, {
                                                            default: _withCtx(() => _cache[6] || (_cache[6] = [
                                                                _createTextVNode("mdi-restore")
                                                            ])),
                                                            _: 1
                                                        }),
                                                        _cache[7] || (_cache[7] = _createElementVNode("span", null, "Reset order", -1))
                                                    ])
                                                ]),
                                                _createVNode(_unref(GridLayout), {
                                                    class: "doc-folder-grid",
                                                    layout: folderLayout.value,
                                                    "onUpdate:layout": _cache[3] || (_cache[3] = ($event) => ((folderLayout).value = $event)),
                                                    "col-num": 12,
                                                    "row-height": 10,
                                                    margin: [16, 16],
                                                    "is-draggable": true,
                                                    "is-resizable": false,
                                                    "vertical-compact": true,
                                                    onLayoutUpdated: onFolderLayoutUpdated
                                                }, {
                                                    default: _withCtx(() => [
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(folderLayout.value, (item) => {
                                                            return (_openBlock(), _createBlock(_unref(GridItem), {
                                                                key: item.i,
                                                                x: item.x,
                                                                y: item.y,
                                                                w: item.w,
                                                                h: item.h,
                                                                i: item.i,
                                                                "drag-allow-from": ".folder-drag-handle"
                                                            }, {
                                                                default: _withCtx(() => [
                                                                    (cardsById.value[item.i])
                                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                                                            _createElementVNode("div", _hoisted_6, [
                                                                                _createVNode(_component_v_icon, { size: "18" }, {
                                                                                    default: _withCtx(() => _cache[8] || (_cache[8] = [
                                                                                        _createTextVNode("mdi-drag")
                                                                                    ])),
                                                                                    _: 1
                                                                                })
                                                                            ]),
                                                                            _createVNode(DocCategoryCard, {
                                                                                label: cardsById.value[item.i].label,
                                                                                illustration: cardsById.value[item.i].illustration,
                                                                                count: categoryCounts.value[item.i] ? categoryCounts.value[item.i] + ' documents' : null,
                                                                                onClick: ($event) => (activeTab.value = item.i)
                                                                            }, null, 8, ["label", "illustration", "count", "onClick"])
                                                                        ]))
                                                                        : _createCommentVNode("", true)
                                                                ]),
                                                                _: 2
                                                            }, 1032, ["x", "y", "w", "h", "i"]));
                                                        }), 128))
                                                    ]),
                                                    _: 1
                                                }, 8, ["layout"]),
                                                (faqs.value.length)
                                                    ? (_openBlock(), _createBlock(FaqsSection, {
                                                        key: 0,
                                                        class: "faq-section",
                                                        faqs: faqs.value,
                                                        limit: 5,
                                                        faqName: "document-faqs"
                                                    }, null, 8, ["faqs"]))
                                                    : _createCommentVNode("", true)
                                            ], 64))
                                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                (groupedDocuments.value.length === 0)
                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                        _cache[9] || (_cache[9] = _createElementVNode("div", { class: "no-filtered-docs" }, [
                                                            _createElementVNode("p", { class: "no-filtered-docs-title" }, "No documents here yet."),
                                                            _createElementVNode("p", { class: "no-filtered-docs-subtitle" }, "Send one and we'll file it here automatically.")
                                                        ], -1)),
                                                        (faqs.value.length)
                                                            ? (_openBlock(), _createBlock(FaqsSection, {
                                                                key: 0,
                                                                class: "faq-section",
                                                                faqs: faqs.value,
                                                                limit: 5,
                                                                faqName: "document-faqs"
                                                            }, null, 8, ["faqs"]))
                                                            : _createCommentVNode("", true)
                                                    ], 64))
                                                    : _createCommentVNode("", true),
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedDocuments.value, (group) => {
                                                    return (_openBlock(), _createElementBlock("div", {
                                                        key: group.label,
                                                        class: "doc-type-group"
                                                    }, [
                                                        _createElementVNode("p", _hoisted_7, _toDisplayString(group.label), 1),
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.docs, (doc) => {
                                                            return (_openBlock(), _createElementBlock("div", {
                                                                key: doc.id,
                                                                class: "doc-row",
                                                                onClick: ($event) => (openDoc(doc.id))
                                                            }, [
                                                                _createElementVNode("div", _hoisted_9, [
                                                                    _createElementVNode("p", _hoisted_10, _toDisplayString(properCase(doc.company || doc.documentSubject)), 1),
                                                                    _createElementVNode("p", _hoisted_11, _toDisplayString(doc.documentName), 1),
                                                                    _createElementVNode("p", _hoisted_12, "Added " + _toDisplayString(doc.createDateTimeString), 1),
                                                                    (doc.documentSubject)
                                                                        ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(doc.documentSubject), 1))
                                                                        : _createCommentVNode("", true)
                                                                ]),
                                                                _createElementVNode("img", {
                                                                    src: require('@/assets/arrow-right.png'),
                                                                    class: "doc-arrow"
                                                                }, null, 8, _hoisted_14)
                                                            ], 8, _hoisted_8));
                                                        }), 128))
                                                    ]));
                                                }), 128))
                                            ], 64))
                                    ], 64))
                            ], 64))
                    ], 64)),
                _createVNode(WhatsAppEnableDrawer, {
                    ref_key: "whatsAppEnableDrawer",
                    ref: whatsAppEnableDrawer,
                    phoneNumber: _unref(state).customer?.person?.mobilePhone || '',
                    onEnable: _unref(handleEnableWhatsApp),
                    onDone: _unref(handleWhatsAppEnableDone),
                    onClosed: _unref(handleWhatsAppDrawerClosed)
                }, null, 8, ["phoneNumber", "onEnable", "onDone", "onClosed"]),
                _createVNode(DocumentDialog, {
                    ref_key: "docDialog",
                    ref: docDialog,
                    documentId: selectedDocId.value,
                    onChanged: onDocChanged
                }, null, 8, ["documentId"])
            ], 2));
        };
    }
});
